import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ChevronLeft, ChevronRight, Calendar, MapPin, ArrowLeft } from 'lucide-react';
import { leaveAPI, restaurantAPI } from '../lib/api';

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function toIso(d) { return d.toISOString().split('T')[0]; }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }

export default function LeaveCalendar() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [view, setView] = useState('month'); // month | week
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    restaurantAPI.getAll().then((r) => setLocations(r.data.restaurants || r.data || [])).catch(() => {});
  }, []);

  const { from, to, days } = useMemo(() => {
    if (view === 'week') {
      const start = new Date(cursor);
      start.setDate(start.getDate() - start.getDay()); // Sunday
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const arr = [];
      for (let i = 0; i < 7; i += 1) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        arr.push(d);
      }
      return { from: toIso(start), to: toIso(end), days: arr };
    }
    // Month view — pad to full 6-week grid
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    const start = new Date(first);
    start.setDate(start.getDate() - first.getDay());
    const arr = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      arr.push(d);
    }
    return { from: toIso(start), to: toIso(arr[arr.length - 1]), days: arr };
  }, [cursor, view]);

  const reload = useCallback(async () => {
    try {
      const params = { start: from, end: to };
      if (locationId) params.location_id = locationId;
      const r = await leaveAPI.calendar(params);
      setItems(r.data.items || []);
    } catch {/* silent */}
  }, [from, to, locationId]);

  useEffect(() => { reload(); }, [reload]);

  // Group items by date for quick lookup
  const byDate = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      const s = new Date(it.start_date);
      const e = new Date(it.end_date);
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const key = toIso(d);
        if (!m[key]) m[key] = [];
        m[key].push(it);
      }
    });
    return m;
  }, [items]);

  const headerLabel = cursor.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  return (
    <Layout>
      <div className="space-y-4" data-testid="leave-calendar-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/leave')} className="mb-1">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Leave
            </Button>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Calendar className="h-6 w-6 text-emerald-600" /> Leave Calendar
            </h1>
            <p className="text-sm text-slate-500 mt-1">Operational wall-chart — approved + pending leave at a glance.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-slate-400" />
              <Select value={locationId || 'all'} onValueChange={(v) => setLocationId(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40" data-testid="cal-location"><SelectValue placeholder="All locations" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-28" data-testid="cal-view"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="week">Week</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setCursor((c) => addMonths(c, -1))} data-testid="cal-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium w-32 text-center">{headerLabel}</span>
            <Button variant="outline" size="sm" onClick={() => setCursor((c) => addMonths(c, 1))} data-testid="cal-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Grid */}
        <div className="rounded-lg border bg-white overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-50 text-xs font-medium text-slate-600">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="px-2 py-1.5 border-b">{d}</div>
            ))}
          </div>
          <div className={`grid grid-cols-7 ${view === 'month' ? 'grid-rows-6' : ''}`}>
            {days.map((d) => {
              const key = toIso(d);
              const dayItems = byDate[key] || [];
              const inMonth = view === 'week' || d.getMonth() === cursor.getMonth();
              const isToday = toIso(new Date()) === key;
              return (
                <div
                  key={key}
                  className={`min-h-[88px] border-b border-r p-1.5 text-xs ${inMonth ? 'bg-white' : 'bg-slate-50/50 text-slate-400'} ${isToday ? 'ring-1 ring-orange-300' : ''}`}
                  data-testid={`cal-cell-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isToday ? 'text-orange-600' : ''}`}>{d.getDate()}</span>
                    {dayItems.length > 0 && (
                      <span className="text-[10px] text-slate-400">{dayItems.length}</span>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayItems.slice(0, 3).map((it) => (
                      <div
                        key={`${it.id}-${key}`}
                        title={`${it.employee_name || ''} — ${it.leave_type_name} (${it.status})`}
                        className={`rounded px-1.5 py-0.5 text-[10px] truncate ${it.status === 'pending' ? 'opacity-60' : ''}`}
                        style={{
                          backgroundColor: (it.display_color || '#94A3B8') + '33',
                          color: '#1E293B',
                          borderLeft: `3px solid ${it.display_color || '#94A3B8'}`,
                        }}
                        data-testid={`cal-leave-${it.id}`}
                      >
                        {it.employee_name || it.leave_type_name}
                      </div>
                    ))}
                    {dayItems.length > 3 && (
                      <div className="text-[10px] text-slate-500">+{dayItems.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
